import { FC, RefObject, useEffect, useState } from "react";
import moment from "moment";
import DatePickerModal from "./DatePickerModal";
import { DatePickerType } from "../types/rentalCarDatesRangeInput.types";
import { useData } from "data/data-provider";

interface MonthsViewProps {
  dialRef: RefObject<HTMLDivElement>;
  isDragging: boolean;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleTouchStart: (e: React.TouchEvent) => void;
  openDatePickerModal: (type: DatePickerType) => void;
  showDatePicker: DatePickerType;
  tempSelectedDate: Date | null;
  handleModalDateChange: (date: Date | null) => void;
  handleModalSave: () => void;
  handleModalClose: () => void;
  monthsToShow: number;
  calendarViewDate: Date | null;
  startDate: Date | null;
  isDateSelectable: (date: Date) => boolean;
  renderDayContents: (day: number, date?: Date) => React.ReactNode;
  handleMonthChange: (date: Date) => void;
  isLoading?: boolean;
}

const MonthsView: FC<MonthsViewProps> = ({
  dialRef,
  isDragging,
  handleMouseDown,
  handleTouchStart,
  openDatePickerModal,
  showDatePicker,
  tempSelectedDate,
  handleModalDateChange,
  handleModalSave,
  handleModalClose,
  monthsToShow,
  calendarViewDate,
  startDate,
  isDateSelectable,
  renderDayContents,
  handleMonthChange,
  isLoading = false,
}) => {
  const { selectedMonth, monthStartDate, monthEndDate } = useData();
  const angle = (selectedMonth / 12) * 360 - 90;
  const monthDots = Array.from({ length: 12 }, (_, i) => {
    const dotAngle = (i / 12) * 360 - 90;
    return { angle: dotAngle, month: i + 1 };
  });

  const circumference = 2 * Math.PI * 42;
  const arcLength = (selectedMonth / 12) * circumference;
  const trackRadius = 42;

  const [arcColor, setArcColor] = useState("#eb1757"); // fallback

  useEffect(() => {
    const div = document.createElement("div");
    div.className = "bg-bg-color";
    document.body.appendChild(div);
    setArcColor(getComputedStyle(div).backgroundColor);
    document.body.removeChild(div);
  }, []);

  return (
    <div className="flex flex-col items-center">
      {/* Circular Dial Container */}
      <div
        ref={dialRef}
        className="relative w-64 h-64 sm:w-80 sm:h-80 mb-2 cursor-pointer select-none touch-none"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        {/* Inner ring shadow effect */}
        <div
          className="absolute rounded-full"
          style={{
            top: "8%",
            left: "8%",
            right: "8%",
            bottom: "8%",
            background: "#f0f0f0",
            boxShadow:
              "inset 8px 8px 16px #d1d1d1, inset -8px -8px 16px #d1d1d1",
          }}
        />

        {/* SVG for arc and dots */}
        <svg
          className="absolute"
          style={{
            top: "8%",
            left: "8%",
            width: "84%",
            height: "84%",
          }}
          viewBox="0 0 100 100"
        >
          <defs>
            <linearGradient
              id="arcGradient"
              gradientUnits="userSpaceOnUse"
              x1="50"
              y1="0"
              x2="100"
              y2="100"
            >
              <stop offset="0%" stopColor={arcColor} />
              <stop offset="10%" stopColor={arcColor} />
              <stop offset="40%" stopColor={arcColor} />
              <stop offset="100%" stopColor={arcColor} />
            </linearGradient>

            <filter id="arcShadow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow
                dx="0"
                dy="2"
                stdDeviation="2"
                floodColor={arcColor}
                floodOpacity="0.3"
              />
            </filter>
          </defs>

          {/* Month indicator dots */}
          {monthDots.map((dot) => {
            const dotX =
              50 + trackRadius * Math.cos((dot.angle * Math.PI) / 180);
            const dotY =
              50 + trackRadius * Math.sin((dot.angle * Math.PI) / 180);

            return (
              <circle
                key={dot.month}
                cx={dotX}
                cy={dotY}
                r="1.2"
                fill="#a1a1aa"
                opacity={1}
              />
            );
          })}

          {/* Progress arc */}
          <circle
            cx="50"
            cy="50"
            r="42"
            fill="none"
            stroke="url(#arcGradient)"
            strokeWidth="15"
            strokeLinecap="round"
            strokeDasharray={`${arcLength} ${circumference}`}
            strokeDashoffset="0"
            transform="rotate(-90 50 50)"
            filter="url(#arcShadow)"
            style={{
              transition: isDragging
                ? "none"
                : "stroke-dasharray 0.3s ease-out",
            }}
          />

          {/* Draggable knob */}
          <circle
            cx={50 + trackRadius * Math.cos((angle * Math.PI) / 180)}
            cy={50 + trackRadius * Math.sin((angle * Math.PI) / 180)}
            r="6"
            fill="white"
            stroke="white"
            strokeWidth="2"
            style={{
              filter: "drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.25))",
              cursor: "grab",
              transition: isDragging
                ? "none"
                : "cx 0. 3s ease-out, cy 0.3s ease-out",
            }}
          />
        </svg>

        {/* Inner white circle with number */}
        <div
          className="absolute rounded-full flex flex-col items-center justify-center"
          style={{
            top: "22%",
            left: "22%",
            right: "22%",
            bottom: "22%",
            background: "linear-gradient(145deg, #ffffff, #f5f5f5)",
            boxShadow:
              "inset 4px 4px 8px rgba(0,0,0,0.06), inset -4px -4px 8px rgba(255,255,255,0.8), 4px 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          <span className="text-5xl sm:text-[96px] font-bold text-gray-800 leading-none">
            {selectedMonth}
          </span>
          <span className="text-lg sm:text-2xl text-gray-400 font-semibold">
            month{selectedMonth > 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* Date Range Display - Clickable */}
      <div className="flex items-center space-x-2 text-base sm:text-lg">
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openDatePickerModal("start");
          }}
          className="font-medium underline underline-offset-4 decoration-1 transition-colors text-gray-700 decoration-gray-400 hover:text-gray-900 hover:decoration-gray-600"
        >
          {moment(monthStartDate).format("ddd, MMM D")}
        </button>
        <span className="text-gray-400 font-normal">to</span>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openDatePickerModal("end");
          }}
          className="font-medium underline underline-offset-4 decoration-1 transition-colors text-gray-700 decoration-gray-400 hover:text-gray-900 hover:decoration-gray-600"
        >
          {moment(monthEndDate).format("ddd, MMM D")}
        </button>
      </div>

      {/* Date Picker Modal */}
      <DatePickerModal
        showDatePicker={showDatePicker}
        tempSelectedDate={tempSelectedDate}
        handleModalDateChange={handleModalDateChange}
        handleModalSave={handleModalSave}
        handleModalClose={handleModalClose}
        monthsToShow={monthsToShow}
        calendarViewDate={calendarViewDate}
        startDate={startDate}
        isDateSelectable={isDateSelectable}
        renderDayContents={renderDayContents}
        handleMonthChange={handleMonthChange}
        isLoading={isLoading}
      />
    </div>
  );
};

export default MonthsView;
